import { useState, useCallback, useMemo } from "react";
import { Effect } from "effect";
import { useTranslation } from "@flight-tools/i18n";
import {
  CabinConfiguration,
  SeatCalculatorServiceLive,
  AIRCRAFT_CONFIGS,
} from "@flight-tools/domain";
import { AircraftSelector } from "./components/AircraftSelector";
import { PassengerConfig } from "./components/PassengerConfig";
import { CapacityAnalysis } from "./components/CapacityAnalysis";
import { CabinLayoutView } from "./components/CabinLayoutView";

export function SeatCalculator() {
  const { t } = useTranslation("seat-calculator");

  // Get the aircraft config (currently only SAS A320 Neo)
  const aircraft = AIRCRAFT_CONFIGS[0];
  if (!aircraft) {
    throw new Error("No aircraft configurations available");
  }

  const [config, setConfig] = useState<CabinConfiguration>(
    new CabinConfiguration({
      businessPassengers: 0,
      economyPassengers: 0,
      lapInfants: 0,
    })
  );

  // Calculate layout using Effect
  const layout = useMemo(() => {
    return Effect.runSync(SeatCalculatorServiceLive.generateLayout(config));
  }, [config]);

  const handleBusinessChange = useCallback((value: number) => {
    setConfig(
      (prev) => new CabinConfiguration({ ...prev, businessPassengers: value })
    );
  }, []);

  const handleEconomyChange = useCallback((value: number) => {
    setConfig(
      (prev) => new CabinConfiguration({ ...prev, economyPassengers: value })
    );
  }, []);

  const handleLapInfantsChange = useCallback((value: number) => {
    setConfig((prev) => new CabinConfiguration({ ...prev, lapInfants: value }));
  }, []);

  return (
    <div className="space-y-8">
      {/* Page Title */}
      <div className="text-center">
        <h1 className="text-4xl font-bold tracking-tight lg:text-5xl">
          <span className="text-gradient">{t("title", { ns: "common" })}</span>
        </h1>
        <p className="mt-2 text-muted-foreground">
          {t("subtitle", { ns: "common" })}
        </p>
      </div>

      {/* Main Grid */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Left Column: Configuration & Analysis */}
        <div className="space-y-6">
          <AircraftSelector selectedAircraft={aircraft} />
          <PassengerConfig
            businessPassengers={config.businessPassengers}
            economyPassengers={config.economyPassengers}
            lapInfants={config.lapInfants}
            onBusinessChange={handleBusinessChange}
            onEconomyChange={handleEconomyChange}
            onLapInfantsChange={handleLapInfantsChange}
          />
          <CapacityAnalysis occupancy={layout.occupancy} config={config} />
        </div>

        {/* Right Column: Cabin Layout */}
        <div>
          <CabinLayoutView layout={layout} />
        </div>
      </div>
    </div>
  );
}
